import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { webFilterService } from '../../../services/webFilter.service';
import { getPreviousMonthPeriod } from './webFilterPeriod';
import WriteCompleteBar from '../../report/WriteCompleteBar';
import './WebFilterManagementSection.css';

const COMPANY_GROUPS = ['세아홀딩스 계열', '세아제강지주 계열'];

/**
 * 유해차단 운영보고서 관리 — DLP/NAC 운영보고서 관리와 동일 패턴.
 * 탭: 보고서 요약/작성완료 / 트래픽 업로드 / 비업무사이트 업로드
 */
const WebFilterManagementSection: React.FC<{ activeTab?: string }> = ({ activeTab = 'wf-report' }) => {
  const { user } = useAuth();
  const isAdminEditor = user?.role === 'admin' || user?.role === 'editor';

  return (
    <div className="wf-admin-section">
      {activeTab === 'wf-report' && <WfReportAdmin isAdminEditor={isAdminEditor} role={user?.role} />}
      {activeTab === 'wf-traffic-upload' && <WfTrafficUploadAdmin isAdminEditor={isAdminEditor} />}
      {activeTab === 'wf-non-business-upload' && <WfNonBusinessUploadAdmin isAdminEditor={isAdminEditor} />}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// 보고서 요약/작성완료 관리
// ────────────────────────────────────────────────────────────

const WfReportAdmin: React.FC<{ isAdminEditor: boolean; role?: string }> = ({ isAdminEditor, role }) => {
  const [period, setPeriod] = useState<string>(getPreviousMonthPeriod);
  const [companyGroup, setCompanyGroup] = useState<string>(COMPANY_GROUPS[0]);
  const [summaryContent, setSummaryContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!period || !companyGroup) return;
    (async () => {
      setIsLoading(true);
      try {
        const s = await webFilterService.getReportSummary(period, companyGroup);
        setSummaryContent(s?.summaryContent ?? '');
      } catch (e: any) {
        toast.error('데이터 로드 실패: ' + (e?.message ?? 'unknown'));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [period, companyGroup]);

  const handleSave = async () => {
    if (!isAdminEditor) {
      toast.error('저장 권한이 없습니다.');
      return;
    }
    try {
      await webFilterService.saveReportSummary(period, companyGroup, summaryContent);
      toast.success('요약 저장 완료');
    } catch (e: any) {
      toast.error('저장 실패: ' + (e?.message ?? ''));
    }
  };

  return (
    <div>
      <h2>유해차단 운영보고서 관리</h2>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
        <label>
          기간(YYYYMM):{' '}
          <input
            type="text"
            value={period}
            onChange={(e) => setPeriod(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={{ width: '8em', padding: '0.3rem' }}
          />
        </label>
        <label>
          계열사 그룹:{' '}
          <select value={companyGroup} onChange={(e) => setCompanyGroup(e.target.value)}>
            {COMPANY_GROUPS.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </label>
      </div>

      <div>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
          요약 내용 (Markdown 지원)
        </label>
        <textarea
          value={summaryContent}
          onChange={(e) => setSummaryContent(e.target.value)}
          disabled={!isAdminEditor || isLoading}
          rows={14}
          style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.9rem', padding: '0.5rem' }}
        />
        <div style={{ marginTop: '1rem' }}>
          <button onClick={handleSave} disabled={!isAdminEditor || isLoading}>
            저장
          </button>
        </div>
      </div>

      {/* 기존 발행하기 자리 — 요약을 저장한 뒤 작성완료만 기록한다 (report-approval-flow SPEC D1) */}
      <WriteCompleteBar
        query={/^\d{6}$/.test(period) ? { pageType: 'WEB_FILTER', period, companyGroup } : null}
        role={role}
        beforeComplete={() => webFilterService.saveReportSummary(period, companyGroup, summaryContent)}
      />
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// 트래픽 엑셀 업로드
// ────────────────────────────────────────────────────────────

const WfTrafficUploadAdmin: React.FC<{ isAdminEditor: boolean }> = ({ isAdminEditor }) => {
  const [period, setPeriod] = useState<string>(getPreviousMonthPeriod);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) {
      toast.error('엑셀 파일을 선택해주세요.');
      return;
    }
    if (!isAdminEditor) {
      toast.error('업로드 권한이 없습니다.');
      return;
    }
    setIsUploading(true);
    try {
      const result = await webFilterService.uploadTrafficExcel(period, file);
      toast.success(`업로드 완료. ${JSON.stringify(result.summary)}`);
      setFile(null);
    } catch (e: any) {
      toast.error('업로드 실패: ' + (e?.response?.data?.message || e.message));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="wf-upload-card">
      <div className="wf-upload-card__head">
        <h2 className="wf-upload-card__title">📊 트래픽 데이터 업로드</h2>
        <a className="wf-download-link" href="/downloads/web-filter-traffic-template.xlsx" download>
          <span aria-hidden>📥</span> 양식 다운로드
        </a>
      </div>
      <p className="wf-upload-card__desc">
        시트명 <code>holdings_blocked_traffic</code>, <code>steel_blocked_traffic</code>
        <br />
        컬럼 Date · Personal/Shared Account - Web · Personal/Shared Account - Netapp
      </p>
      <div className="wf-upload-form">
        <div className="wf-field">
          <label className="wf-field__label" htmlFor="wf-traffic-period">기간 (YYYYMM)</label>
          <input
            id="wf-traffic-period"
            className="wf-input"
            type="text"
            value={period}
            onChange={(e) => setPeriod(e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={!isAdminEditor || isUploading}
          />
        </div>
        <div className="wf-field">
          <label className="wf-field__label" htmlFor="wf-traffic-file">엑셀 파일</label>
          <input
            id="wf-traffic-file"
            className="wf-file"
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={!isAdminEditor || isUploading}
          />
        </div>
        <button className="wf-btn-upload" onClick={handleUpload} disabled={!file || !isAdminEditor || isUploading}>
          {isUploading ? '업로드 중…' : '업로드'}
        </button>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// 비업무사이트 엑셀 업로드
// ────────────────────────────────────────────────────────────

const WfNonBusinessUploadAdmin: React.FC<{ isAdminEditor: boolean }> = ({ isAdminEditor }) => {
  const [period, setPeriod] = useState<string>(getPreviousMonthPeriod);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) {
      toast.error('엑셀 파일을 선택해주세요.');
      return;
    }
    if (!isAdminEditor) {
      toast.error('업로드 권한이 없습니다.');
      return;
    }
    setIsUploading(true);
    try {
      const result = await webFilterService.uploadNonBusinessSitesExcel(period, file);
      toast.success(`업로드 완료. 총 ${result.totalInserted}건`);
      setFile(null);
    } catch (e: any) {
      toast.error('업로드 실패: ' + (e?.response?.data?.message || e.message));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="wf-upload-card">
      <div className="wf-upload-card__head">
        <h2 className="wf-upload-card__title">🌐 비업무사이트 검토 내역 업로드</h2>
        <a className="wf-download-link" href="/downloads/web-filter-non-business-sites-template.xlsx" download>
          <span aria-hidden>📥</span> 양식 다운로드
        </a>
      </div>
      <p className="wf-upload-card__desc">
        시트명 <code>holdings_NonBusinessSites</code>, <code>steel_NonBusinessSites</code>
        <br />
        컬럼 No. · Company · Host · Category · Issue
        <br />
        <span className="wf-upload-card__warn">주의</span> 업로드 시 동일 기간/그룹의 기존 데이터는 모두 삭제 후 재입력됩니다.
      </p>
      <div className="wf-upload-form">
        <div className="wf-field">
          <label className="wf-field__label" htmlFor="wf-nonbiz-period">기간 (YYYYMM)</label>
          <input
            id="wf-nonbiz-period"
            className="wf-input"
            type="text"
            value={period}
            onChange={(e) => setPeriod(e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={!isAdminEditor || isUploading}
          />
        </div>
        <div className="wf-field">
          <label className="wf-field__label" htmlFor="wf-nonbiz-file">엑셀 파일</label>
          <input
            id="wf-nonbiz-file"
            className="wf-file"
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={!isAdminEditor || isUploading}
          />
        </div>
        <button className="wf-btn-upload" onClick={handleUpload} disabled={!file || !isAdminEditor || isUploading}>
          {isUploading ? '업로드 중…' : '업로드'}
        </button>
      </div>
    </div>
  );
};

export default WebFilterManagementSection;
